import React, { Fragment, useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import Link from "arena/components/Link";
import { useViewer } from "core/components/ViewerProvider";
import { useArena } from "core/components/ArenaProvider";
import UserMenu from "./UserMenu";
import HeaderLogo from "./HeaderLogo";
import HeaderMenu from "./HeaderMenu";
import useScrollTrigger from "@mui/material/useScrollTrigger";
import classNames from "classnames";
import { isTelegramMiniApp, launchParams } from "core/utils/telegram";
import OpenInWeb from "arena/components/OpenInWeb";
import { useMatch } from "react-router-dom";

// Хук для отслеживания направления скролла
function useScrollDirection() {
  const [scrollDirection, setScrollDirection] = useState(null);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [scrolledPastThreshold, setScrolledPastThreshold] = useState(false);

  useEffect(() => {
    const updateScrollDirection = () => {
      const scrollY = window.pageYOffset;
      const direction = scrollY > lastScrollY ? "down" : "up";

      // Проверяем порог скролла
      setScrolledPastThreshold(scrollY > 20);

      if (
        direction !== scrollDirection &&
        Math.abs(scrollY - lastScrollY) > 10
      ) {
        setScrollDirection(direction);
      }

      setLastScrollY(scrollY > 0 ? scrollY : 0);
    };

    window.addEventListener("scroll", updateScrollDirection);
    return () => window.removeEventListener("scroll", updateScrollDirection);
  }, [scrollDirection, lastScrollY]);

  return { scrollDirection, scrolledPastThreshold };
}

// Хук для определения открыт ли диалог с роутом
function useIsDialogOpen() {
  const videoMatch = useMatch("/video/:contentId");
  const streamMatch = useMatch("/stream/:contentId");
  const postMatch = useMatch("/post/:postId");
  const digitalAssetMatch = useMatch("/digitalAsset/:digitalAssetId");
  const productMatch = useMatch("/product/:productId");

  return !!(
    videoMatch ||
    streamMatch ||
    postMatch ||
    digitalAssetMatch ||
    productMatch
  );
}

const PREFIX = "Header";

const classes = {
  avatarButton: `${PREFIX}-avatarButton`,
  avatar: `${PREFIX}-avatar`,
  menuButton: `${PREFIX}-menuButton`,
  title: `${PREFIX}-title`,
  search: `${PREFIX}-search`,
  signInDesktop: `${PREFIX}-signInDesktop`,
  signInMobile: `${PREFIX}-signInMobile`,
  buttonLabel: `${PREFIX}-buttonLabel`,
  searchIcon: `${PREFIX}-searchIcon`,
  arenaIcon: `${PREFIX}-arenaIcon`,
  appBar: `${PREFIX}-appBar`,
  appBarElevate: `${PREFIX}-appBarElevate`,
  rightMenu: `${PREFIX}-rightMenu`,
};

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  transition: "all ease 0.5s, transform 0.3s ease-in-out",
  zIndex: 1200, // Больше чем у Cover компонентов, но меньше чем у модальных диалогов (1300)
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.03)"
      : "rgba(0, 0, 0, 0.03)",
  backdropFilter: theme.palette.mode === "dark" ? "blur(5px)" : "blur(5px)",

  "&:before": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    height: 100,
    pointerEvents: "none",
    transition: "all ease 1s",
    backgroundImage:
      theme.palette.mode === "dark"
        ? "linear-gradient(rgba(16,16,16,0.8), rgba(16,16,16,0.4), rgba(16,16,16,0.1), transparent)"
        : "linear-gradient(rgba(255,255,255,0.8), rgba(255,255,255,0.6),rgba(255,255,255,0.3), rgba(255,255,255,0.1), transparent)",
  },

  [`&.${classes.appBarElevate}`]: {
    backgroundColor: "rgba(255, 255, 255, 0)",
    boxShadow: "none",
  },

  "&.header-hidden": {
    transform: "translateY(-100%)",
  },

  [`& .${classes.avatarButton}`]: {
    padding: 9,
    [theme.breakpoints.up("sm")]: {
      padding: 4,
    },
  },

  [`& .${classes.avatar}`]: {
    width: 30,
    height: 30,
    borderRadius: 30,

    [theme.breakpoints.up("sm")]: {
      width: 40,
      height: 40,
      borderRadius: 40,
    },
  },

  [`& .${classes.menuButton}`]: {
    // marginRight: 10,

    [theme.breakpoints.up("md")]: {
      display: "none",
    },
  },

  [`& .${classes.title}`]: {
    marginLeft: 10,
  },

  [`& .${classes.search}`]: {
    flex: 1,
    margin: theme.spacing(0, 4),
    marginRight: "auto",
    // background: "#101010",
    maxWidth: 240,
    padding: "4px 10px",
    borderRadius: 4,
  },

  [`& .${classes.signInDesktop}`]: {
    display: "none",

    [theme.breakpoints.up("sm")]: {
      display: "flex",
    },
  },

  [`& .${classes.signInMobile}`]: {
    display: "flex",
    [theme.breakpoints.up("sm")]: {
      display: "none",
    },
  },

  [`& .${classes.buttonLabel}`]: {
    whiteSpace: "nowrap",
  },

  [`& .${classes.searchIcon}`]: {
    [theme.breakpoints.up("sm")]: {
      marginRight: 10,
    },
  },

  [`& .${classes.arenaIcon}`]: {
    [`@media (max-width: 350px)`]: {
      display: "none",
    },
  },

  // [`& .${classes.rightMenu}`]: {
  //   marginLeft: "auto",
  //   display: "flex",
  //   alignItems: "center",
  //   flexWrap: "nowrap",
  // },
}));

export default function Header({
  menuItems,
  onClickMenuMobile,
  onClickSignIn,
  onClickSearch,
  onClickMenuItem,
}) {
  const { t } = useTranslation("arena");
  const viewer = useViewer();
  const arena = useArena();
  const { scrollDirection, scrolledPastThreshold } = useScrollDirection();
  const isDialogOpen = useIsDialogOpen();

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });

  const isDesktopMiniApp =
    isTelegramMiniApp && !["android", "ios"].includes(launchParams?.platform);

  // Определяем, должен ли хедер быть скрыт
  // Не скрываем хедер, если открыт диалог
  const shouldHideHeader =
    arena?.hideableHeader &&
    scrollDirection === "down" &&
    scrolledPastThreshold &&
    !isDialogOpen;

  return (
    <StyledAppBar
      color="default"
      position="fixed"
      className={classNames({
        [classes.appBarElevate]: !trigger,
        "header-hidden": shouldHideHeader,
      })}
      sx={(theme) => ({
        ...(isTelegramMiniApp && {
          [theme.breakpoints.up("md")]: {
            pl: 9,
            pr: 6,
          },
        }),
      })}
    >
      <Toolbar
        disableGutters
        sx={{ pl: { xs: 0.5, sm: 3 }, pr: { xs: 0.5, sm: 3 } }}
      >
        <Tooltip title={t("header.menu")}>
          <IconButton
            onClick={onClickMenuMobile}
            // edge={"start"}
            className={classes.menuButton}
            size="large"
            sx={{ mr: 0.5 }}
          >
            <MenuIcon />
          </IconButton>
        </Tooltip>

        <HeaderLogo arena={arena} />

        <HeaderMenu arena={arena} onClickMenuItem={onClickMenuItem} />

        <Stack spacing={0} direction="row" sx={{ marginLeft: "auto" }}>
          {isDesktopMiniApp && <OpenInWeb />}

          <Tooltip title={t("header.search")}>
            <IconButton
              component={Link}
              to="/search"
              className={classes.searchIcon}
              onClick={onClickSearch}
              size="large"
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>

          {typeof viewer !== "undefined" && arena.isRegistrationEnabled && (
            <>
              {viewer ? (
                <UserMenu viewer={viewer} menuItems={menuItems} />
              ) : (
                <>
                  <Button
                    variant="outlined"
                    onClick={onClickSignIn}
                    className={classes.signInDesktop + " js-sign-in"}
                    classes={{
                      label: classes.buttonLabel,
                    }}
                  >
                    {t("header.signIn")}
                  </Button>

                  <IconButton
                    onClick={onClickSignIn}
                    className={classes.signInMobile + " js-sign-in"}
                    edge="end"
                    size="large"
                  >
                    <AccountCircleIcon />
                  </IconButton>
                </>
              )}
            </>
          )}
        </Stack>
      </Toolbar>
    </StyledAppBar>
  );
}

Header.propTypes = {
  className: PropTypes.string,
  onClickMenuItem: PropTypes.func,
  onClickSearch: PropTypes.func,
  onClickMenuMobile: PropTypes.func,
  onClickSignIn: PropTypes.func,
  menuItems: PropTypes.array,
};
